import React, { useState, useContext } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, Typography, TextField, Grid, Alert, CircularProgress
} from '@mui/material';
import { enquiryService } from '../api/axios';
import { AuthContext } from '../context/AuthContext';

function AddItemModal({ enquiryId, enquiryUniqueId, onClose, onItemsAdded }) {
  const { user } = useContext(AuthContext);
  const [itemData, setItemData] = useState({
    name: '',
    description: '',
    quantity: '',
    budgetPerUnit: '',
    deliveryLocation: '',
    targetDateOfDelivery: ''
  });
  const [itemFiles, setItemFiles] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setItemData(prevData => ({ ...prevData, [name]: value }));
  };

  const handleFilesChange = (e) => {
    setItemFiles(Array.from(e.target.files));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setIsSubmitting(true);

    // Basic validation
    if (!itemData.name.trim() || !itemData.description.trim()) {
      setError("Product name and description are required.");
      setIsSubmitting(false);
      return;
    }

    const formData = new FormData();
    formData.append('item', new Blob([JSON.stringify({
      ...itemData,
      quantity: itemData.quantity ? parseInt(itemData.quantity, 10) : null,
      budgetPerUnit: itemData.budgetPerUnit ? parseFloat(itemData.budgetPerUnit) : null,
    })], { type: 'application/json' }));

    itemFiles.forEach(file => {
      formData.append('files', file);
    });

    try {
      await enquiryService.post(`/enquiry/${enquiryId}/item`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setMessage('Enquiry item added successfully!');
      setItemData({
        name: '', description: '', quantity: '', budgetPerUnit: '',
        deliveryLocation: '', targetDateOfDelivery: ''
      });
      setItemFiles([]);
      if (onItemsAdded) {
        onItemsAdded(); // Callback to refresh parent component (BDDashboard)
      }
      setTimeout(onClose, 2000); // Close modal after success message
    } catch (err) {
      console.error("Error adding enquiry item:", err.response || err);
      setError(`Failed to add item: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h2" component="h2">Add New Item to Enquiry {enquiryUniqueId}</Typography>
          <Button onClick={onClose} color="inherit" sx={{ minWidth: 'auto', p: 1 }}>&times;</Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="name"
                label="Product Name*"
                value={itemData.name}
                onChange={handleInputChange}
                required
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="quantity"
                label="Quantity"
                type="number"
                value={itemData.quantity}
                onChange={handleInputChange}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="budgetPerUnit"
                label="Budget Per Unit"
                type="number"
                step="0.01"
                value={itemData.budgetPerUnit}
                onChange={handleInputChange}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="deliveryLocation"
                label="Delivery Location"
                value={itemData.deliveryLocation}
                onChange={handleInputChange}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="targetDateOfDelivery"
                label="Target Delivery Date"
                type="date"
                value={itemData.targetDateOfDelivery}
                onChange={handleInputChange}
                InputLabelProps={{
                  shrink: true,
                }}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="description"
                label="Product Description*"
                value={itemData.description}
                onChange={handleInputChange}
                required
                multiline
                rows={3}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <label htmlFor="item-file-input">
                <input
                  style={{ display: 'none' }}
                  id="item-file-input"
                  type="file"
                  multiple // Allow multiple files
                  onChange={handleFilesChange}
                  accept="image/*,application/pdf"
                />
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                >
                  {itemFiles.length > 0 ? `${itemFiles.length} file(s) selected` : 'Attach Files to Item (Optional, Max 10)'}
                </Button>
                {itemFiles.length > 0 && (
                  <Box sx={{ mt: 1, p: 1, border: '1px dashed #ccc', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>Selected Files:</Typography>
                    {itemFiles.map((file, index) => (
                        <Typography key={index} variant="caption" display="block">{file.name}</Typography>
                    ))}
                  </Box>
                )}
              </label>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0, flexDirection: 'column', alignItems: 'flex-end' }}>
        {message && <Alert severity="success" sx={{ mb: 2, width: '100%' }}>{message}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2, width: '100%' }}>{error}</Alert>}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          disabled={isSubmitting}
          onClick={handleSubmit}
          sx={{ mt: 2 }}
        >
          {isSubmitting ? <CircularProgress size={24} /> : 'Add Item'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddItemModal;